import { CreditCard, UserRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";
import type { ReceiptPrefill } from "@/routes/_app/-components/financeiro/financeiro-v2/types";
import type { CardSettlementDetail } from "@/types/financeiro";

export function SettlementOrders({
	settlement,
	onIdentify,
}: {
	settlement: CardSettlementDetail;
	onIdentify: (receipt: ReceiptPrefill) => void;
}) {
	if (settlement.credit_orders.length === 0) {
		return (
			<div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
				<CreditCard className="mx-auto size-5 text-muted-foreground" />
				<p className="mt-2 text-sm font-semibold text-foreground">Detalhamento indisponível</p>
				<p className="mt-1 text-xs text-muted-foreground">
					A Rede ainda não retornou as ordens de crédito deste repasse.
				</p>
			</div>
		);
	}
	const chargesLabel =
		settlement.charges.length === 1
			? "1 débito compõe este pagamento"
			: `${settlement.charges.length} débitos compõem este pagamento`;

	return (
		<div className="flex flex-col gap-3">
			{settlement.charges.length > 0 && (
				<section className="rounded-lg border border-border bg-secondary/35 px-4 py-3">
					<div className="flex items-center justify-between gap-4">
						<div>
							<p className="text-xs font-semibold text-foreground">Ajustes descontados pela Rede</p>
							<p className="text-xs text-muted-foreground">{chargesLabel}</p>
						</div>
						<p className="font-mono text-xs font-semibold text-destructive">
							-{FinanceiroLabels.money(settlement.charge_cents)}
						</p>
					</div>
				</section>
			)}
			{settlement.credit_orders.map((order) => (
				<section
					key={order.external_id}
					className="overflow-hidden rounded-lg border border-border"
				>
					<div className="flex flex-wrap items-center justify-between gap-3 bg-secondary/50 px-4 py-3">
						<div>
							<p className="text-xs font-semibold text-foreground">
								Resumo {order.sale_summary_number}
							</p>
							<p className="text-xs text-muted-foreground">Ordem {order.credit_order_number}</p>
							{!order.sales_complete && (
								<p className="mt-1 text-xs font-semibold text-destructive">
									{order.sales_difference_cents > 0 &&
										`Faltam ${FinanceiroLabels.money(order.sales_difference_cents)} no detalhamento`}
									{order.sales_difference_cents < 0 &&
										`O detalhamento excede a ordem em ${FinanceiroLabels.money(-order.sales_difference_cents)}`}
								</p>
							)}
						</div>
						<div className="grid grid-cols-3 gap-5 text-right">
							<OrderAmount label="Bruto" cents={order.gross_cents} />
							<OrderAmount label="Taxas" cents={order.fee_cents} />
							<OrderAmount label="Líquido" cents={order.net_cents} />
						</div>
					</div>

					{order.sales.length === 0 && (
						<p className="px-4 py-5 text-xs text-muted-foreground">
							Nenhuma venda ou parcela da Rede corresponde a esta ordem.
						</p>
					)}

					<div className="divide-y divide-border">
						{order.sales.map((sale) => (
							<div key={sale.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
								<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
									<UserRound className="size-4" />
								</div>
								<div className="min-w-40 flex-1">
									<p className="truncate text-xs font-semibold text-foreground">
										{sale.person_name ?? "Paciente não identificado"}
									</p>
									<p className="text-xs text-muted-foreground">
										{sale.service_type ?? sale.product}
									</p>
									<p className="text-xs text-muted-foreground">
										{format.date(sale.sale_on)} às {sale.sale_time.slice(0, 5)}
									</p>
								</div>
								<SaleAmounts sale={sale} />
								{sale.status === "cancelled" && (
									<span className="text-xs font-semibold text-destructive">Venda cancelada</span>
								)}
								{sale.chargeback_status !== null &&
									sale.chargeback_status !== "CHARGEBACK_SOLVED_NO_IMPACT" && (
										<span className="text-xs font-semibold text-destructive">Chargeback</span>
									)}
								{sale.receipt_id === null &&
									sale.status === "approved" &&
									(sale.chargeback_status === null ||
										sale.chargeback_status === "CHARGEBACK_SOLVED_NO_IMPACT") && (
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												onIdentify({
													method: sale.method,
													amount_cents: sale.gross_cents,
													received_on: sale.sale_on,
													declared_installment_count: sale.installment_quantity,
													description: sale.product,
													card_sale_id: sale.id,
												})
											}
										>
											Identificar paciente
										</Button>
									)}
							</div>
						))}
					</div>
				</section>
			))}
		</div>
	);
}

function SaleAmounts({
	sale,
}: {
	sale: CardSettlementDetail["credit_orders"][number]["sales"][number];
}) {
	if (sale.installment_quantity === 1) {
		return (
			<div className="text-right">
				<p className="text-xs text-muted-foreground">Neste repasse</p>
				<p className="font-mono text-xs font-semibold text-foreground">
					{FinanceiroLabels.money(sale.allocation_gross_cents)}
				</p>
			</div>
		);
	}
	const totalLabel = sale.receipt_id === null ? "Total da venda" : "Recebimento do paciente";

	return (
		<div className="grid min-w-44 gap-2 text-right">
			<div>
				<p className="text-xs text-muted-foreground">{totalLabel}</p>
				<p className="font-mono text-xs font-semibold text-foreground">
					{FinanceiroLabels.money(sale.receipt_amount_cents ?? sale.gross_cents)}
				</p>
			</div>
			<div>
				<p className="text-xs text-muted-foreground">
					Parcela {sale.installment_number} de {sale.installment_quantity} neste repasse
				</p>
				<p className="font-mono text-xs font-semibold text-primary">
					{FinanceiroLabels.money(sale.allocation_gross_cents)}
				</p>
			</div>
		</div>
	);
}

function OrderAmount({ label, cents }: { label: string; cents: number }) {
	return (
		<div>
			<p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
			<p className="mt-0.5 font-mono text-xs font-semibold text-foreground">
				{FinanceiroLabels.money(cents)}
			</p>
		</div>
	);
}
